'use client';

import { Button } from '@get-mosh/msh-shared';
import type { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import EventNames from 'src/analytics/EventNames';
import { trackClick } from 'src/analytics/Events';
import { PrismicNextImage } from '@prismicio/next';
import classNames from 'classnames';
import { PrismicRichText } from '@prismicio/react';
import { backgroundColourVariants } from 'src/constants/style';

export type FooterBannerProps = SliceComponentProps<Content.FooterBannerSlice>;

const FooterBanner = ({
    slice,
    context,
}: FooterBannerProps & { context: { pageType: string } }) => (
    <section
        className={classNames(
            slice.primary.sticky
                ? 'transparent'
                : backgroundColourVariants[slice.primary.background],
            {
                'sticky bottom-0 w-full': slice.primary.sticky,
            },
        )}
    >
        {slice.variation === 'defaultV2' ? (
            <div
                className={classNames(
                    'container flex flex-col justify-center gap-6 py-8 md:flex-row md:gap-x-12 md:py-16',
                    {
                        'justify-center':
                            Object.keys(slice.primary.image).length === 0,
                    },
                )}
            >
                <div
                    className={classNames(
                        'flex flex-col gap-6 space-y-2 self-stretch pt-8 md:h-[371px] md:w-[1400px]',
                        {
                            'items-center justify-center text-center':
                                Object.keys(slice.primary.image).length === 0,
                        },
                    )}
                >
                    <div className="flex flex-col gap-2">
                        {slice.primary.pre_heading ? (
                            <h4 className="text-grey-400 md:leading-[65px]">
                                {slice.primary.pre_heading}
                            </h4>
                        ) : null}
                        <h2 className="text-[32px] leading-[43px] md:text-xxl md:leading-[65px]">
                            {slice.primary.heading}
                        </h2>
                        <p className="text-md text-grey-400 md:leading-loose">
                            {slice.primary.description}
                        </p>
                    </div>
                    <div className="flex w-full flex-col items-center justify-center gap-4">
                        {slice.primary.cta_label && slice.primary.cta_path ? (
                            <Button
                                text={slice.primary.cta_label}
                                as="a"
                                href={slice.primary.cta_path}
                                stretch
                                onClick={() => {
                                    trackClick(EventNames.BUTTON_CLICKED, {
                                        pageType: context.pageType,
                                        label: slice.primary.cta_label || '',
                                        link: slice.primary.cta_path || '',
                                        variation: slice.variation,
                                        location: slice.slice_type,
                                    });
                                }}
                            />
                        ) : null}
                        <PrismicRichText
                            field={slice.primary.terms_and_conditions}
                        />
                    </div>
                </div>
                {slice.primary.image ? (
                    <PrismicNextImage
                        field={slice.primary.image}
                        className="h-[185px] rounded-[999px] object-cover md:h-[371px] md:w-[660px]"
                    />
                ) : null}
            </div>
        ) : null}
        {slice.variation === 'sticky' ? (
            <div
                className={classNames(
                    'mx-auto flex max-h-24 flex-row items-center justify-between gap-4 rounded-t-3xl border border-grey-200 p-4 shadow-xl md:w-[640px] md:gap-8 md:p-6',
                    backgroundColourVariants[slice.primary.background],
                )}
            >
                <div className={classNames('flex flex-col gap-1')}>
                    <h5>{slice.primary.heading}</h5>
                    <p className="p2 leading-[19px] text-grey-400">
                        {slice.primary.description}
                    </p>
                </div>
                {slice.primary.cta_label && slice.primary.cta_path ? (
                    <div>
                        <Button
                            text={slice.primary.cta_label}
                            as="a"
                            href={slice.primary.cta_path}
                            onClick={() => {
                                trackClick(EventNames.BUTTON_CLICKED, {
                                    pageType: context.pageType,
                                    label: slice.primary.cta_label || '',
                                    link: slice.primary.cta_path || '',
                                    variation: slice.variation,
                                    location: slice.slice_type,
                                });
                            }}
                        />
                    </div>
                ) : null}
            </div>
        ) : null}
    </section>
);

export default FooterBanner;
